import omit from 'lodash/omit';

import JsonSchema, { JsonSchemaComplexObject } from '@evidentid/json-schema/interfaces/JsonSchema';
import { TprmSchema } from '@evidentid/tprm-portal-lib/models/common/TprmSchema.model';
import { TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

import { convertCustomPropertySchemaToEnumSchema } from '@/modules/decisioning-criteria/utils/criterionSchemaConversion';
import { filterCustomPropertiesBySchema } from '@/modules/decisioning-criteria/utils/filterCustomPropertiesBySchema';
import { removeNonStandardSchemaFormat } from '@/modules/decisioning-criteria/utils/removeNonStandardSchemaFormat';

/** True when this branch holds a custom-property picker that no property of the client's would match,
 * i.e. `filterBy` was inferred from the opposite branch and filters everything out. A null `filterBy`
 * means this branch is not a picker at all.
 *
 * A picker that declares its own `insuredFieldFilterSchema` is excluded: the inferred filter is not
 * what that picker is actually filtered by, so it says nothing about whether the picker is usable.
 * Those are the multi-branch selectors (e.g. limitSource's three-way radio) that have no single
 * opposite branch to infer from -- see applyDeclaredPickerFilters in standardizeCustomPropertySchema.
 */
function pickerHasNoOptions(
    pickerSchema: JsonSchema | undefined,
    filterBy: JsonSchema | null,
    customProperties?: CustomProperty[],
): boolean {
    if (!filterBy || (pickerSchema as any)?.insuredFieldFilterSchema) {
        return false;
    }
    return filterCustomPropertiesBySchema(customProperties || [], filterBy).length === 0;
}

/** `bound` names the references whose stored value already points at a custom property; their wrapper
 * is kept even when nothing matches, so the binding stays visible and editable rather than being
 * dropped. `allowed` is how that decision reaches the node itself, one level down from the key.
 */
interface CollapseOptions {
    bound: Set<string>;
    allowed: boolean;
}

const collapseEverything: CollapseOptions = { bound: new Set(), allowed: true };

/** What an if/then/else node's two branches mean for standardisation: the schema each picker should be
 * filtered by (inferred from the opposite branch), and the branch to collapse to when a picker can
 * offer the client nothing at all.
 */
interface ConditionalBranches {
    thenFilterBy: JsonSchema | null;
    elseFilterBy: JsonSchema | null;
    unusablePickerStaticBranch: JsonSchema | null;
}

function inspectConditionalBranches(
    // Narrowed by the caller's `'if' in schema`, which yields JsonSchemaObject rather than
    // JsonSchemaComplexObject -- the latter requires `if`/`then`/`else` to be present rather than
    // merely possible, so taking it here would reject the very node the caller has just checked.
    schema: JsonSchema | JsonSchemaComplexObject,
    customProperties?: CustomProperty[],
): ConditionalBranches {
    const conditional = schema as JsonSchemaComplexObject;
    const thenSchema = Object.values(conditional.then?.properties || {})[0];
    const elseSchema = Object.values(conditional.else?.properties || {})[0];
    const isPicker = (branch: JsonSchema) =>
        (branch as TprmSchema)?.displayFormat === TprmSchemaDisplayFormat.insuredField;

    const thenFilterBy = isPicker(thenSchema) ? elseSchema : null;
    const elseFilterBy = isPicker(elseSchema) ? thenSchema : null;

    let staticBranch: JsonSchema | null = null;
    if (pickerHasNoOptions(thenSchema, thenFilterBy, customProperties)) {
        staticBranch = conditional.else as JsonSchema;
    } else if (pickerHasNoOptions(elseSchema, elseFilterBy, customProperties)) {
        staticBranch = conditional.then as JsonSchema;
    }

    return { thenFilterBy, elseFilterBy, unusablePickerStaticBranch: staticBranch };
}

function standardizeCustomPropertySchema(
    schema: JsonSchema | JsonSchemaComplexObject,
    customProperties?: CustomProperty[],
    schemaFilterBy?: JsonSchema | null,
    collapse: CollapseOptions = collapseEverything,
): JsonSchema {
    let moddedSchema = schema;

    if ('if' in schema) {
        const { thenFilterBy, elseFilterBy, unusablePickerStaticBranch } =
            inspectConditionalBranches(schema, customProperties);

        // PRODUCT-18727: a client with no custom property of this criterion's value type cannot use
        // the picker at all, so the whole choice is dropped rather than offering a "Set To Field"
        // checkbox whose dropdown would be empty. Collapsing to the static branch also removes the
        // checkbox, which the row-level `hasMatchingCustomProperty` guard in RiskProfileCriterion.vue
        // cannot do here -- that guard only covers criteria declaring a conditionalValueReference,
        // and a ValueOrInsuredField criterion renders its checkbox from this schema instead.
        const staticBranch = collapse.allowed ? unusablePickerStaticBranch : null;

        if (staticBranch) {
            // Keeps only the static alternative's properties. A value already stored as
            // { isBasedOnInsuredField: false, value: X } still round-trips: the flag is simply not
            // rendered, and ValueOrInsuredField decodes { value: X } as Value(X) either way.
            return standardizeCustomPropertySchema(
                {
                    ...omit(schema, [ 'if', 'then', 'else' ]),
                    properties: (staticBranch as JsonSchemaComplexObject).properties,
                    required: (staticBranch as any).required,
                } as JsonSchema,
                customProperties,
                null,
                collapse,
            );
        }

        moddedSchema = {
            ...schema,
            if: standardizeCustomPropertySchema(schema.if as JsonSchema, customProperties, null, collapse),
            then: standardizeCustomPropertySchema(schema.then as JsonSchema, customProperties, thenFilterBy, collapse),
            else: standardizeCustomPropertySchema(schema.else as JsonSchema, customProperties, elseFilterBy, collapse),
        } as JsonSchemaComplexObject;
    }

    if ('properties' in moddedSchema) {
        return {
            ...moddedSchema,
            properties: Object.keys(moddedSchema.properties).reduce((acc, key) => ({
                ...acc,
                // Criterion references are the top-level properties of the schema buildCriterionSchema
                // produces, so a bound reference is recognised by its key here.
                [key]: standardizeCustomPropertySchema(
                    (moddedSchema as JsonSchemaComplexObject).properties[key],
                    customProperties,
                    schemaFilterBy,
                    { bound: collapse.bound, allowed: !collapse.bound.has(key) },
                ),
            }), {}),
        };
    } else if ('items' in moddedSchema) {
        return {
            ...moddedSchema,
            items: standardizeCustomPropertySchema(
                moddedSchema.items as JsonSchema,
                customProperties,
                null,
                collapse,
            ),
        };
    }

    const isCustomPropertySchema =
        (moddedSchema as TprmSchema).displayFormat === TprmSchemaDisplayFormat.insuredField &&
        customProperties && customProperties.length > 0;

    if (isCustomPropertySchema) {
        // A picker may declare the schema it wants to be filtered by. The inferred filter comes from
        // the opposite branch of the enclosing if/then/else, which is empty for settings that have no
        // static alternative — those must say what they accept rather than accepting everything.
        const declaredFilterBy = (moddedSchema as any).insuredFieldFilterSchema as JsonSchema | undefined;
        return convertCustomPropertySchemaToEnumSchema(
            omit(moddedSchema, [ 'insuredFieldFilterSchema' ]) as JsonSchema,
            customProperties,
            declaredFilterBy ?? schemaFilterBy,
        );
    }

    return moddedSchema;
}

export function standardizeCriterionSchema(
    schema: JsonSchema,
    customProperties?: CustomProperty[],
    schemaFilterBy?: JsonSchema | null,
    boundReferences?: Set<string>,
): JsonSchema {
    return removeNonStandardSchemaFormat(
        standardizeCustomPropertySchema(schema, customProperties, schemaFilterBy,
            boundReferences ? { bound: boundReferences, allowed: true } : collapseEverything),
    );
}
